import { DebugLogger } from '@affine/debug';
import { UNTITLED_WORKSPACE_NAME } from '@affine/env/constant';
import { UserFriendlyError } from '@affine/error';
import {
  createWorkspaceMutation,
  deleteWorkspaceMutation,
  getWorkspaceInfoQuery,
  getWorkspacesQuery,
  Permission,
  ServerFeature,
} from '@affine/graphql';
import {
  type BlobStorage,
  type DocStorage,
  type ListedBlobRecord,
  universalId,
} from '@affine/nbstore';
import {
  CloudBlobStorage,
  isCloudBackendDegradationError,
  resolveCloudHttpEndpoint,
  StaticCloudDocStorage,
} from '@affine/nbstore/cloud';
import {
  IndexedDBBlobStorage,
  IndexedDBBlobSyncStorage,
  IndexedDBDocStorage,
  IndexedDBDocSyncStorage,
} from '@affine/nbstore/idb';
import {
  IndexedDBV1BlobStorage,
  IndexedDBV1DocStorage,
} from '@affine/nbstore/idb/v1';
import {
  SqliteBlobStorage,
  SqliteBlobSyncStorage,
  SqliteDocStorage,
  SqliteDocSyncStorage,
} from '@affine/nbstore/sqlite';
import {
  SqliteV1BlobStorage,
  SqliteV1DocStorage,
} from '@affine/nbstore/sqlite/v1';
import type { UnboundWorkerInitOptions } from '@affine/nbstore/worker/client';
import { localAccountKeyError } from '@streamlain/view-config/local-data-seal';
import {
  backoffRetry,
  catchErrorInto,
  effect,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  ObjectPool,
  onComplete,
  onStart,
  Service,
} from '@toeverything/infra';
import { isEqual } from 'lodash-es';
import { map, Observable, switchMap, tap } from 'rxjs';
import {
  applyUpdate,
  type Array as YArray,
  Doc as YDoc,
  encodeStateAsUpdate,
  type Map as YMap,
} from 'yjs';

import {
  isBackendKnownUnavailable,
  subscribeBackendAvailabilityUi,
} from '../../../utils/backend-availability';
import { opfsBlobCache } from '../../../utils/opfs-blob-cache';
import { resolveSocketBaseUrl } from '../../../utils/socket';
import type { Server, ServerConfig, ServersService } from '../../cloud';
import {
  AccountChanged,
  AuthService,
  defaultScopedDegradationStore,
  GraphQLService,
  workspaceApiFetch,
  WorkspaceServerService,
} from '../../cloud';
import { DesktopApiService } from '../../desktop-api';
import { LocalAccountKeys } from '../../local-data-keys/local-account-keys';
import { type GlobalState, NbstoreProvider } from '../../storage';
import type {
  LocalEraseOptions,
  Workspace,
  WorkspaceFlavourProvider,
  WorkspaceFlavoursProvider,
  WorkspaceMetadata,
  WorkspaceOpenOptions,
  WorkspaceProfileInfo,
} from '../../workspace';
import { WorkspaceImpl } from '../../workspace/impls/workspace';
import {
  resolveWorkspaceBlobFromCacheOrRemote,
  resolveWorkspaceBlobMetadata,
  workspaceBlobCacheToken,
  type WorkspaceBlobContext,
  WorkspaceBlobLocalStorageUnavailableError,
  workspaceBlobNamespace,
} from '../../workspace/utils/blob-cache';
import {
  type CachedWorkspacePermissionCacheScope,
  isCachedWorkspacePermissionCacheScopeFresh,
  workspacePermissionCacheScopeKey,
} from '../../workspace/utils/permission-cache-identity';
import { getWorkspaceProfileWorker } from './out-worker';
import {
  type PendingWorkspaceCreation,
  pendingWorkspaceCreationKey,
  readPendingWorkspaceCreation,
  resolveWorkspaceCreationAttempt,
} from './workspace-creation-idempotency';

const getCloudWorkspaceCacheKey = (serverId: string) => {
  if (serverId === 'streamlain-cloud') {
    return 'cloud-workspace:'; // FOR BACKWARD COMPATIBILITY
  }
  return `selfhosted-workspace-${serverId}:`;
};

const logger = new DebugLogger('affine:cloud-workspace-flavour-provider');

function getCloudWorkspaceAuthScope(accountId: string | undefined) {
  return accountId ? `account:${accountId}` : 'anonymous';
}

function getCloudWorkspaceLocalStorageId(
  workspaceId: string,
  authScope: string
) {
  return `${workspaceId}:${authScope}`;
}

function createWorkspaceBlobFetch(
  serverBaseUrl: string,
  workspaceId: string
): typeof fetch {
  return (input, init) => {
    const request =
      typeof input === 'string' && input.startsWith('/')
        ? resolveCloudHttpEndpoint(serverBaseUrl, input)
        : input;
    return workspaceApiFetch(request, init, {
      workspaceId,
      category:
        init?.method && init.method !== 'GET'
          ? 'foreground-action'
          : 'workspace-api',
      key: `workspace-blob:${init?.method ?? 'GET'}:${String(request)}`,
      mode: init?.method && init.method !== 'GET' ? 'foreground' : 'background',
      priority:
        init?.method && init.method !== 'GET' ? 'foreground' : 'visible',
    });
  };
}

/**
 * sl-k7r: Helper to wait for connection with timeout.
 * Prevents hanging forever if connection fails.
 */
const CONNECTION_TIMEOUT_MS = 30000; // 30 seconds

async function waitForConnectedWithTimeout(
  connection: { waitForConnected: () => Promise<void> },
  context: string
): Promise<void> {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(
        new Error(
          `Connection timeout after ${CONNECTION_TIMEOUT_MS}ms: ${context}`
        )
      );
    }, CONNECTION_TIMEOUT_MS);
  });

  try {
    await Promise.race([connection.waitForConnected(), timeoutPromise]);
  } catch (err) {
    logger.error(`[sl-k7r] ${context}`, err);
    throw err;
  }
}

export class CloudWorkspaceFlavourProvider implements WorkspaceFlavourProvider {
  private readonly authService: AuthService;
  private readonly graphqlService: GraphQLService;
  private readonly unsubscribeAccountChanged: () => void;
  private readonly unsubscribeBackendAvailability: () => void;
  private readonly workspaceRoles = new Map<string, Permission>();
  private readonly memberBlobContexts = new Map<string, WorkspaceBlobContext>();
  private pendingWorkspaceCreation: PendingWorkspaceCreation | undefined;

  constructor(
    private readonly globalState: GlobalState,
    private readonly server: Server,
    private readonly localAccountKeys: LocalAccountKeys,
    private readonly desktopApi: DesktopApiService | undefined,
    private readonly nbstoreProvider?: NbstoreProvider
  ) {
    this.authService = server.scope.get(AuthService);
    this.graphqlService = server.scope.get(GraphQLService);
    this.unsubscribeAccountChanged = this.server.scope.eventBus.on(
      AccountChanged,
      account => {
        this.pendingWorkspaceCreation = undefined;
        if (!account && !isBackendKnownUnavailable()) {
          this.clearCachedPermissionScope();
        }
        this.revalidate();
      }
    );
    this.unsubscribeBackendAvailability = subscribeBackendAvailabilityUi(() => {
      if (isBackendKnownUnavailable()) {
        this.revalidate();
      }
    });
  }

  readonly flavour = this.server.id;

  DocStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocStorage
      : IndexedDBDocStorage;
  DocStorageV1Type = BUILD_CONFIG.isElectron
    ? SqliteV1DocStorage
    : BUILD_CONFIG.isWeb || BUILD_CONFIG.isMobileWeb
      ? IndexedDBV1DocStorage
      : undefined;
  BlobStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteBlobStorage
      : IndexedDBBlobStorage;
  BlobStorageV1Type = BUILD_CONFIG.isElectron
    ? SqliteV1BlobStorage
    : BUILD_CONFIG.isWeb || BUILD_CONFIG.isMobileWeb
      ? IndexedDBV1BlobStorage
      : undefined;
  DocSyncStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteDocSyncStorage
      : IndexedDBDocSyncStorage;
  BlobSyncStorageType =
    BUILD_CONFIG.isElectron || BUILD_CONFIG.isIOS || BUILD_CONFIG.isAndroid
      ? SqliteBlobSyncStorage
      : IndexedDBBlobSyncStorage;

  async deleteWorkspace(id: string): Promise<void> {
    await this.graphqlService.gql({
      query: deleteWorkspaceMutation,
      variables: {
        id: id,
      },
    });
    // TODO(@forehalo): when deleting cloud workspace, should we delete the workspace folder in local?
    this.revalidate();
    await this.waitForLoaded().catch(() => undefined);
  }

  async eraseLocalData(id: string, options?: LocalEraseOptions): Promise<void> {
    if (!this.nbstoreProvider) throw new Error('Local store is unavailable');
    const localStorageId = this.getLocalStorageId(id);
    const accountId = this.authService.session.account$.value?.id;
    // The store worker holds the local databases for every tab: it closes
    // them, deletes the IndexedDB ones and waits for the deletion.
    const erased = await this.nbstoreProvider.eraseSpace(
      id,
      options?.recoveryVersion
    );
    if (!erased) return;
    // Desktop doc/blob storages are SQLite; the search index stays IndexedDB.
    if (BUILD_CONFIG.isElectron) {
      if (!this.desktopApi || !accountId) throw localAccountKeyError('LOCKED');
      await this.desktopApi.handler.workspace.delete(
        universalId({
          peer: this.flavour,
          type: 'workspace',
          id: localStorageId,
        }),
        { accountKey: accountId }
      );
    }
    await opfsBlobCache.clearWorkspace(
      workspaceBlobNamespace({
        flavour: this.flavour,
        type: 'workspace',
        id: localStorageId,
      })
    );
  }

  async createWorkspace(
    initial: (
      docCollection: WorkspaceImpl,
      blobStorage: BlobStorage,
      docStorage: DocStorage
    ) => Promise<void>,
    name?: string,
    locale?: string
  ): Promise<WorkspaceMetadata> {
    const accountId = this.authService.session.account$.value?.id;
    const pendingKey = pendingWorkspaceCreationKey(this.server.id, accountId);
    const attempt = resolveWorkspaceCreationAttempt(
      this.pendingWorkspaceCreation ??
        readPendingWorkspaceCreation(this.globalState.get(pendingKey)),
      name,
      locale
    );
    this.pendingWorkspaceCreation = attempt;
    this.globalState.set(pendingKey, attempt);
    let completed = false;
    try {
      // Keep the same key until both server provisioning and local bootstrap
      // finish. A lost response or local-storage failure can then resume the
      // same authoritative workspace instead of creating a duplicate.
      const result = await this.graphqlService.gql({
        query: createWorkspaceMutation,
        variables: {
          idempotencyKey: attempt.idempotencyKey,
          name,
          locale,
        },
      });
      const { id: workspaceId } = (result as any).createWorkspace;
      const localStorageId = this.getLocalStorageId(workspaceId);
      const localBlobOptions =
        await this.resolveLocalStorageOptions(workspaceId);
      const blobContext: WorkspaceBlobContext = {
        local: localBlobOptions,
        external: false,
        assertActive: () => {
          if (this.getLocalStorageId(workspaceId) !== localStorageId) {
            throw new DOMException('Workspace identity changed', 'AbortError');
          }
        },
        canReadPending: () => true,
      };
      const blobNamespace = workspaceBlobNamespace(blobContext.local);

      // save the initial state to local storage, then sync to cloud
      const blobStorage = new this.BlobStorageType(localBlobOptions);
      blobStorage.connection.connect();
      await waitForConnectedWithTimeout(
        blobStorage.connection,
        'blobStorage create'
      );
      const docStorage = new this.DocStorageType(localBlobOptions);
      docStorage.connection.connect();
      await waitForConnectedWithTimeout(
        docStorage.connection,
        'docStorage create'
      );

      const docList = new Set<YDoc>();

      const docCollection = new WorkspaceImpl({
        id: workspaceId,
        rootDoc: new YDoc({ guid: workspaceId }),
        blobSource: {
          get: async key => {
            blobContext.assertActive();
            const file = await opfsBlobCache.getCompleteFile(
              blobNamespace,
              key
            );
            blobContext.assertActive();
            return file;
          },
          getMetadata: async key => {
            return resolveWorkspaceBlobMetadata({
              workspaceId,
              key,
              context: blobContext,
            });
          },
          delete: async () => {
            return;
          },
          list: async () => {
            return [];
          },
          set: async (id, blob) => {
            blobContext.assertActive();
            const cached = await opfsBlobCache.set(blobNamespace, id, blob, {
              mime: blob.type,
              size: blob.size,
              source: 'upload',
              cacheToken: workspaceBlobCacheToken(id, blob.size),
            });
            if (!cached) {
              throw new WorkspaceBlobLocalStorageUnavailableError();
            }
            blobContext.assertActive();
            await blobStorage.set({
              key: id,
              source: blob,
              mime: blob.type,
              size: blob.size,
            });
            return id;
          },
          name: 'blob',
          readonly: false,
        },
        onLoadDoc: doc => {
          docList.add(doc);
        },
      });

      try {
        // apply initial state
        await initial(docCollection, blobStorage, docStorage);

        // save workspace to local storage, should be vary fast
        for (const subdocs of docList) {
          await docStorage.pushDocUpdate({
            docId: subdocs.guid,
            bin: encodeStateAsUpdate(subdocs),
          });
        }

        await this.writeInitialDocProperties(
          workspaceId,
          docStorage,
          accountId ?? ''
        );

        if (accountId) {
          this.rememberCreatedWorkspace(accountId, workspaceId);
        }
        this.revalidate();
      } finally {
        docStorage.connection.disconnect();
        blobStorage.connection.disconnect();
        docCollection.dispose();
      }

      completed = true;
      return {
        id: workspaceId,
        flavour: this.server.id,
      };
    } finally {
      if (
        completed &&
        this.pendingWorkspaceCreation?.idempotencyKey === attempt.idempotencyKey
      ) {
        this.pendingWorkspaceCreation = undefined;
        this.globalState.del(pendingKey);
      }
    }
  }
  revalidate = effect(
    map(() => {
      return { accountId: this.authService.session.account$.value?.id };
    }),
    exhaustMapSwitchUntilChanged(
      (a, b) => a.accountId === b.accountId,
      ({ accountId }) => {
        return fromPromise(async signal => {
          if (!accountId) {
            // Before the first session response a missing account means
            // "unknown", not "signed out". Stay revalidating until the
            // session is known; a signed-in result switches this effect to
            // the account key via AccountChanged.
            await this.authService.session.hasRevalidated$.waitFor(
              hasRevalidated => hasRevalidated,
              signal
            );
            return null; // no cloud workspace if no account
          }

          const { workspaces } = await this.graphqlService.gql({
            query: getWorkspacesQuery,
            context: {
              signal,
            },
          });

          const ids = workspaces.map(
            ({ id, initialized, role, team, deletionScheduledAt }) => ({
              id,
              initialized,
              role,
              team,
              deletionScheduledAt,
            })
          );
          return {
            accountId,
            workspaces: ids.map(
              ({ id, initialized, role, team, deletionScheduledAt }) => ({
                id,
                flavour: this.server.id,
                initialized,
                role,
                team,
                deletionScheduledAt,
              })
            ),
          };
        }).pipe(
          // A transient failure (5xx, 429, network) must not end the
          // revalidation: without a cached list the workspace route would read
          // "loaded, empty" and redirect a cold deep link away from its
          // workspace. 401 is an answer and is not retried.
          backoffRetry({
            when: error => !UserFriendlyError.fromAny(error).isStatus(401),
          }),
          tap(data => {
            if (data) {
              const { accountId, workspaces } = data;
              const sorted = this.sortWorkspaces(workspaces);
              this.cacheWorkspaces(accountId, sorted);
              this.setWorkspaceRoles(sorted);
              if (!isEqual(this.workspaces$.value, sorted)) {
                this.workspaces$.next(sorted);
              }
            } else {
              this.workspaces$.next(this.getOfflineWorkspaces());
            }
          }),
          catchErrorInto(this.error$, err => {
            logger.error('error to revalidate cloud workspaces', err);
          }),
          onStart(() => this.isRevalidating$.next(true)),
          onComplete(() => this.isRevalidating$.next(false))
        );
      },
      ({ accountId }) => {
        const cached = this.getScopedCachedWorkspaces(accountId);
        this.setWorkspaceRoles(cached);
        this.workspaces$.next(cached);
      }
    )
  );

  error$ = new LiveData<any>(null);
  isRevalidating$ = new LiveData(false);
  workspaces$ = new LiveData<WorkspaceMetadata[]>([]);

  private sortWorkspaces(workspaces: WorkspaceMetadata[]): WorkspaceMetadata[] {
    return [...workspaces].sort((a, b) => a.id.localeCompare(b.id));
  }

  private setWorkspaceRoles(workspaces: WorkspaceMetadata[]): void {
    this.workspaceRoles.clear();
    for (const workspace of workspaces) {
      if (workspace.role) {
        this.workspaceRoles.set(workspace.id, workspace.role);
      }
    }
  }

  private rememberCreatedWorkspace(
    accountId: string,
    workspaceId: string
  ): void {
    const workspace: WorkspaceMetadata = {
      id: workspaceId,
      flavour: this.server.id,
      role: Permission.Owner,
      deletionScheduledAt: null,
    };
    const workspaces = this.sortWorkspaces([
      ...this.getScopedCachedWorkspaces(accountId).filter(
        existing => existing.id !== workspaceId
      ),
      workspace,
    ]);
    this.cacheWorkspaces(accountId, workspaces);
    this.setWorkspaceRoles(workspaces);
    this.workspaces$.next(workspaces);
  }

  private cacheWorkspaces(
    accountId: string,
    workspaces: WorkspaceMetadata[]
  ): void {
    this.globalState.set(
      getCloudWorkspaceCacheKey(this.server.id) + accountId,
      workspaces
    );
    this.globalState.set<CachedWorkspacePermissionCacheScope>(
      workspacePermissionCacheScopeKey(this.server.id),
      {
        accountId,
        cachedAt: Date.now(),
        serverBaseUrl: this.server.baseUrl,
        serverId: this.server.id,
        workspaces,
      }
    );
  }

  private getLocalStorageId(workspaceId: string): string {
    return getCloudWorkspaceLocalStorageId(
      workspaceId,
      getCloudWorkspaceAuthScope(this.authService.session.account$.value?.id)
    );
  }

  private getCachedPermissionScope(): CachedWorkspacePermissionCacheScope | null {
    const scope =
      this.globalState.get<CachedWorkspacePermissionCacheScope>(
        workspacePermissionCacheScopeKey(this.server.id)
      ) ?? null;
    if (
      !isCachedWorkspacePermissionCacheScopeFresh(scope, {
        serverBaseUrl: this.server.baseUrl,
        serverId: this.server.id,
      })
    ) {
      return null;
    }
    return scope;
  }

  private getOfflineWorkspaces(): WorkspaceMetadata[] {
    if (!isBackendKnownUnavailable()) {
      return [];
    }
    return this.getScopedCachedWorkspaces();
  }

  private getScopedCachedWorkspaces(accountId?: string): WorkspaceMetadata[] {
    const scope = this.getCachedPermissionScope();
    if (accountId && scope?.accountId !== accountId) {
      return [];
    }
    return scope ? this.sortWorkspaces(scope.workspaces) : [];
  }

  private clearCachedPermissionScope(): void {
    this.globalState.del(workspacePermissionCacheScopeKey(this.server.id));
  }

  async getWorkspaceProfile(
    id: string,
    signal?: AbortSignal
  ): Promise<WorkspaceProfileInfo | undefined> {
    const info = await this.getWorkspaceInfo(id, signal);
    const workspace = info.workspace;
    const workspaceProfile = {
      name: workspace.name,
      avatar: workspace.avatarUrl ?? undefined,
      isOwner: workspace.role === Permission.Owner,
      isAdmin: workspace.role === Permission.Admin,
      membershipKind: workspace.membershipKind,
      isTeam: workspace.team,
      role: workspace.role,
    };

    if (!workspaceProfile.isOwner && !workspaceProfile.isAdmin) {
      return {
        ...workspaceProfile,
        isEmpty: false,
      };
    }

    // Root-YDoc profile data is privileged metadata. Non-admin members receive
    // the authoritative DB display metadata above and must not probe root docs.
    // Only the server copy is read here: the local copy is sealed with account
    // keys that live in the workspace engine worker, not on this thread.
    const cloudStorage = new StaticCloudDocStorage({
      id: id,
      serverBaseUrl: this.server.serverMetadata.baseUrl,
    });
    let cloudData: Uint8Array | undefined;
    let cloudDegradationCode: string | undefined;
    try {
      cloudData = (await cloudStorage.getDoc(id))?.bin;
    } catch (error) {
      if (!isCloudBackendDegradationError(error)) {
        throw error;
      }
      cloudDegradationCode = error.code;
      defaultScopedDegradationStore.markDegraded({
        key: { scope: 'metadata', workspaceId: id },
        code: error.code,
        message: error.message,
        requestId: error.degradation?.requestId,
        retryable: error.degradation?.retryable,
        severity: error.degradation?.userVisibleSeverity ?? 'inline',
      });
    }

    const isEmpty = isEmptyUpdate(cloudData);

    if (!cloudData) {
      return {
        ...workspaceProfile,
        isEmpty,
        cloudDegraded: !!cloudDegradationCode,
        cloudDegradationCode,
      };
    }

    const client = getWorkspaceProfileWorker();

    const result = await client.call('renderWorkspaceProfile', [cloudData]);
    const rootName =
      result.name &&
      (result.name !== UNTITLED_WORKSPACE_NAME || !workspaceProfile.name)
        ? result.name
        : undefined;

    return {
      ...workspaceProfile,
      name: rootName ?? workspaceProfile.name,
      avatar: result.avatar ?? workspaceProfile.avatar,
      isEmpty,
      cloudDegraded: !!cloudDegradationCode,
      cloudDegradationCode,
    };
  }

  async getWorkspaceBlob(id: string, blob: string): Promise<Blob | null> {
    return resolveWorkspaceBlobFromCacheOrRemote({
      workspaceId: id,
      key: blob,
      context: this.memberBlobContext(id),
      fetchImpl: createWorkspaceBlobFetch(
        this.server.serverMetadata.baseUrl,
        id
      ),
    });
  }

  /**
   * The signed-in member's blob scope of a workspace on this device: one
   * context per scope, so the cache hits of a start are checked in one batch.
   */
  private memberBlobContext(id: string): WorkspaceBlobContext {
    if (!this.authService.session.account$.value?.id) {
      throw new DOMException(
        'Initialized account blob scope required',
        'AbortError'
      );
    }
    const localStorageId = this.getLocalStorageId(id);
    const current = this.memberBlobContexts.get(id);
    if (current?.local.id === localStorageId) return current;
    const context: WorkspaceBlobContext = {
      local: { flavour: this.flavour, type: 'workspace', id: localStorageId },
      external: false,
      assertActive: () => {
        if (
          !this.authService.session.account$.value?.id ||
          this.getLocalStorageId(id) !== localStorageId
        ) {
          throw new DOMException('Workspace identity changed', 'AbortError');
        }
      },
      canReadPending: () => true,
    };
    this.memberBlobContexts.set(id, context);
    return context;
  }

  async listBlobs(id: string): Promise<ListedBlobRecord[]> {
    const cloudStorage = new CloudBlobStorage({
      id,
      serverBaseUrl: this.server.serverMetadata.baseUrl,
    });
    return cloudStorage.list();
  }

  async deleteBlob(
    id: string,
    blob: string,
    permanent: boolean
  ): Promise<void> {
    const cloudStorage = new CloudBlobStorage({
      id,
      serverBaseUrl: this.server.serverMetadata.baseUrl,
    });
    await cloudStorage.delete(blob, permanent);

    // should also delete from local storage
    const storage = new this.BlobStorageType(
      await this.resolveLocalStorageOptions(id)
    );
    storage.connection.connect();
    await waitForConnectedWithTimeout(storage.connection, 'blobStorage delete');
    await storage.delete(blob, permanent);
    storage.connection.disconnect();
  }

  onWorkspaceInitialized(workspace: Workspace): void {
    // bind the workspace to the affine cloud server
    workspace.scope.get(WorkspaceServerService).bindServer(this.server);
    if (!workspace.openOptions.externalDocAccess) {
      const context = workspace.blobContext;
      const assertActive = context.assertActive;
      context.assertActive = () => {
        assertActive();
        if (this.getLocalStorageId(workspace.id) !== context.local.id) {
          throw new DOMException('Workspace identity changed', 'AbortError');
        }
      };
    }
  }

  private async getWorkspaceInfo(workspaceId: string, signal?: AbortSignal) {
    return await this.graphqlService.gql({
      query: getWorkspaceInfoQuery,
      variables: {
        workspaceId,
      },
      context: { signal },
    });
  }

  private getSocketServerBaseUrl() {
    const storedConfig = this.globalState.get<ServerConfig>(
      `serverConfig:${this.server.id}`
    );
    return resolveSocketBaseUrl({
      syncUrl:
        this.server.serverMetadata.syncUrl ??
        storedConfig?.syncUrl ??
        this.server.config$.value?.syncUrl,
      baseUrl: this.server.baseUrl,
    });
  }

  private async resolveLocalStorageOptions(workspaceId: string) {
    return {
      flavour: this.flavour,
      type: 'workspace' as const,
      id: this.getLocalStorageId(workspaceId),
      authBinding: await this.localAccountKeys.resolveBinding({
        flavour: this.flavour,
        workspaceId,
      }),
    };
  }

  getEngineWorkerInitOptions(
    workspaceId: string,
    _metadata?: WorkspaceMetadata,
    openOptions?: WorkspaceOpenOptions
  ): UnboundWorkerInitOptions {
    const socketServerBaseUrl = this.getSocketServerBaseUrl();
    const externalAccess = openOptions?.externalDocAccess;
    const authScope = externalAccess
      ? [
          'external',
          externalAccess.identityId,
          encodeURIComponent(workspaceId),
          encodeURIComponent(externalAccess.docId),
        ].join(':')
      : getCloudWorkspaceAuthScope(this.authService.session.account$.value?.id);
    const localStorageId = getCloudWorkspaceLocalStorageId(
      workspaceId,
      authScope
    );
    return {
      identityKey: `auth:${authScope}`,
      local: {
        doc: {
          name: this.DocStorageType.identifier,
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
        blob: {
          name: this.BlobStorageType.identifier,
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
        docSync: {
          name: this.DocSyncStorageType.identifier,
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
        blobSync: {
          name: this.BlobSyncStorageType.identifier,
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
        awareness: {
          name: 'BroadcastChannelAwarenessStorage',
          opts: {
            id: `${this.flavour}:${localStorageId}`,
          },
        },
        indexer: {
          name: 'IndexedDBIndexerStorage',
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
        indexerSync: {
          name: 'IndexedDBIndexerSyncStorage',
          opts: {
            flavour: this.flavour,
            type: 'workspace',
            id: localStorageId,
          },
        },
      },
      remotes: {
        [`cloud:${this.flavour}`]: {
          doc: {
            name: 'CloudDocStorage',
            opts: {
              type: 'workspace',
              id: workspaceId,
              // Doc writes are authorized per document by the sync gateway.
              // Workspace Reader is a server-side ceiling for doc writes.
              readonlyMode: false,
              // sl-m3s8: WebSocket connections use dedicated sync endpoint
              serverBaseUrl: socketServerBaseUrl,
              authScope,
            },
          },
          blob: {
            name: 'CloudBlobStorage',
            opts: {
              id: workspaceId,
              serverBaseUrl: this.server.serverMetadata.baseUrl,
              externalDocAccess: externalAccess
                ? { docId: externalAccess.docId }
                : undefined,
            },
          },
          awareness: {
            name: 'CloudAwarenessStorage',
            opts: {
              type: 'workspace',
              id: workspaceId,
              // sl-m3s8: WebSocket connections use dedicated sync endpoint
              serverBaseUrl: socketServerBaseUrl,
              authScope,
            },
          },
          indexer: this.server.config$.value.features.includes(
            ServerFeature.Indexer
          )
            ? {
                name: 'CloudIndexerStorage',
                opts: {
                  flavour: this.flavour,
                  type: 'workspace',
                  id: workspaceId,
                  serverBaseUrl: this.server.serverMetadata.baseUrl,
                },
              }
            : undefined,
        },
        v1: {
          doc: this.DocStorageV1Type
            ? {
                name: this.DocStorageV1Type.identifier,
                opts: {
                  id: workspaceId,
                  type: 'workspace',
                },
              }
            : undefined,
          blob: this.BlobStorageV1Type
            ? {
                name: this.BlobStorageV1Type.identifier,
                opts: {
                  id: workspaceId,
                  type: 'workspace',
                },
              }
            : undefined,
        },
      },
      // sl-suoi.1.3: Enable selective sync for cloud workspaces.
      // Empty array = selective mode active, Hot Sync auto-selects Top-10
      // from local timestamps (sl-95dg.1.2).
      // WorkspaceDB system docs (db$docProperties, db$folders etc.) are connected
      // dynamically via WorkspaceDBService.connectDoc() with priority 50.
      initialDocIds: externalAccess ? [externalAccess.docId] : [],
    };
  }

  async writeInitialDocProperties(
    workspaceId: string,
    docStorage: DocStorage,
    creatorId: string
  ) {
    const rootDocBuffer = await docStorage.getDoc(workspaceId);
    const rootDoc = new YDoc({ guid: workspaceId });
    if (rootDocBuffer) {
      applyUpdate(rootDoc, rootDocBuffer.bin);
    }

    const docIds = (rootDoc.getMap('meta').get('pages') as YArray<YMap<string>>)
      ?.map(page => page.get('id'))
      .filter(Boolean) as string[];

    const propertiesDBBuffer = await docStorage.getDoc('db$docProperties');
    const propertiesDB = new YDoc({ guid: 'db$docProperties' });
    if (propertiesDBBuffer) {
      applyUpdate(propertiesDB, propertiesDBBuffer.bin);
    }

    for (const docId of docIds) {
      const docProperties = propertiesDB.getMap(docId);
      docProperties.set('id', docId);
      docProperties.set('createdBy', creatorId);
    }

    await docStorage.pushDocUpdate({
      docId: 'db$docProperties',
      bin: encodeStateAsUpdate(propertiesDB),
    });
  }

  private waitForLoaded() {
    return this.isRevalidating$.waitFor(loading => !loading);
  }

  dispose() {
    this.revalidate.unsubscribe();
    this.unsubscribeAccountChanged();
    this.unsubscribeBackendAvailability();
  }
}

export class CloudWorkspaceFlavoursProvider
  extends Service
  implements WorkspaceFlavoursProvider
{
  constructor(
    private readonly globalState: GlobalState,
    private readonly serversService: ServersService,
    private readonly localAccountKeys: LocalAccountKeys
  ) {
    super();
  }

  workspaceFlavours$ = LiveData.from<WorkspaceFlavourProvider[]>(
    this.serversService.servers$.pipe(
      switchMap(servers => {
        const refs = servers.map(server => {
          const exists = this.pool.get(server.id);
          if (exists) {
            return exists;
          }
          const provider = new CloudWorkspaceFlavourProvider(
            this.globalState,
            server,
            this.localAccountKeys,
            this.framework.getOptional(DesktopApiService),
            this.framework.getOptional(NbstoreProvider)
          );
          provider.revalidate();
          const ref = this.pool.put(server.id, provider);
          return ref;
        });

        return new Observable<WorkspaceFlavourProvider[]>(subscribe => {
          subscribe.next(refs.map(ref => ref.obj));
          return () => {
            refs.forEach(ref => {
              ref.release();
            });
          };
        });
      })
    ),
    [] as any
  );

  private readonly pool = new ObjectPool<string, CloudWorkspaceFlavourProvider>(
    {
      onDelete(obj) {
        obj.dispose();
      },
    }
  );
}

export function isEmptyUpdate(binary: Uint8Array | undefined) {
  if (!binary) {
    return true;
  }
  return (
    binary.byteLength === 0 ||
    (binary.byteLength === 2 && binary[0] === 0 && binary[1] === 0)
  );
}
